<template>
    <div class="container bg" :style="bgStyle">
      <!-- PWA安装按钮 -->
      <button 
        v-if="showInstallButton && !isK8Domain" 
        class="install-btn"
        @click="installPWA"
        type="button"
      >
        <el-icon style="margin-right: 5px;">
          <Download />
        </el-icon>
        <span>安装APP</span>
      </button>
      
      <form @submit.prevent="onLogin" class="main gap-10 ac-1 ac-2">
        <div class="input-group">
          <el-icon class="input-icon">
            <Avatar />
          </el-icon>
          <input 
            class="input-field" 
            type="text" 
            name="username"
            placeholder="请输入用户名"
            v-model="account"
            autocomplete="username"
          />
        </div>

        <div class="input-group">
          <el-icon class="input-icon">
            <Lock />
          </el-icon>
          <input 
            class="input-field" 
            type="password" 
            name="password"
            placeholder="请输入密码"
            v-model="password"
            autocomplete="current-password"
          />
        </div>

        <div class="button-group mt-30">
          <button class="btn btn-primary btn-large" type="submit">
            <span>登 录</span>
          </button>
        </div>

        <div class="secondary-buttons mt-20">
          <button class="btn btn-text" type="button" @click="gotoRegister">
            <span>注 册</span>
          </button>
          <button 
            v-if="!isK8Domain" 
            class="btn btn-text" 
            type="button" 
            @click="gotoRoute"
          >
            <span>线 路</span>
          </button>
        </div>
      </form>
    </div>
</template>

<script>
// @ is an alias to /src
import { useGameStore } from '@/store/game.js'
import { computed, onMounted, ref } from 'vue'
import { useRouter } from 'vue-router'
import { getBgImage, isK8Brand, brand } from '@/config/brand.js'

export default {
  name: 'LoginView',
  setup() {
    const router = useRouter()
    const gameStore = useGameStore()
    
    const account = ref('')
    const password = ref('')
    const showInstallButton = ref(false)
    const deferredPrompt = ref(null)
    const isIOS = ref(false)
    const isAndroid = ref(false)
    // 使用构建时确定的品牌配置
    const isK8Domain = ref(isK8Brand)
    
    // 检查URL参数是否包含 app=1
    const isAppMode = () => {
      const params = new URLSearchParams(window.location.search)
      return params.get('app') === '1'
    }
    
    // 检查是否已登录
    const checkToken = () => {
      const token = gameStore.getToken()
      if (token) {
        // 延迟跳转，确保PWA按钮逻辑执行完
        setTimeout(() => {
          router.push('/plaza')
        }, 100)
      }
    }
    
    // 检测是否为iOS设备
    const detectIOS = () => {
      const userAgent = window.navigator.userAgent.toLowerCase()
      return /iphone|ipad|ipod/.test(userAgent)
    }
    
    // 检测是否为Android设备
    const detectAndroid = () => {
      const userAgent = window.navigator.userAgent.toLowerCase()
      // 简单直接的Android检测：userAgent包含android
      // 移动设备的userAgent通常包含"mobile"或"android"
      const isAndroidUA = /android/.test(userAgent)
      // 进一步确认：移动设备通常有触摸支持
      const hasTouchSupport = 'ontouchstart' in window || navigator.maxTouchPoints > 0
      
      // Android设备：userAgent包含android，且有触摸支持（排除桌面Chrome）
      return isAndroidUA && hasTouchSupport
    }
    
    // 检查是否为PWA模式
    const isPWA = () => {
      // 检查多个条件来确定是否真的在PWA模式下运行
      const isStandalone = window.matchMedia('(display-mode: standalone)').matches
      const isIOSStandalone = window.navigator.standalone === true
      
      return isStandalone || isIOSStandalone
    }
    
    // 组件挂载时检查
    onMounted(() => {
      isIOS.value = detectIOS()
      isAndroid.value = detectAndroid()
      
      // k8品牌直接隐藏安装按钮
      if (isK8Brand) {
        showInstallButton.value = false
      }
      
      // 如果 app=1 参数存在，不显示按钮
      if (isAppMode()) {
        showInstallButton.value = false
        checkToken()
        return
      }
      
      // 先检查是否已经安装了PWA（适用于所有设备）
      const pwaMode = isPWA()
      
      // 如果已经是PWA模式，不显示安装按钮（所有设备通用）
      if (pwaMode) {
        showInstallButton.value = false
        checkToken()
        return
      }
      
      // Android设备：检查是否已安装，如果未安装则显示按钮
      if (isAndroid.value) {
        // 再次检查PWA模式（确保未安装）
        if (isPWA()) {
          showInstallButton.value = false
          checkToken()
          return
        }
        
        // 监听beforeinstallprompt事件以获取安装能力
        const beforeInstallHandler = (e) => {
          // 再次检查是否已安装PWA
          if (isPWA()) {
            showInstallButton.value = false
            return
          }
          e.preventDefault()
          deferredPrompt.value = e
          // 当beforeinstallprompt触发时，说明可以安装，显示按钮
          if (!showInstallButton.value && !isPWA()) {
            showInstallButton.value = true
          }
        }
        window.addEventListener('beforeinstallprompt', beforeInstallHandler)
        
        // 即使beforeinstallprompt未触发，也显示按钮（某些Android浏览器可能不支持该事件）
        // 延迟一点显示，给beforeinstallprompt事件一些时间触发
        setTimeout(() => {
          // 再次检查是否已安装PWA
          if (!isPWA() && !showInstallButton.value) {
            showInstallButton.value = true
          }
        }, 500)
        
        // 监听appinstalled事件
        window.addEventListener('appinstalled', () => {
          showInstallButton.value = false
          deferredPrompt.value = null
        })
        
        // 最后检查token（避免太早跳转）
        checkToken()
        return
      }
      
      // iOS设备：直接显示安装按钮
      if (isIOS.value) {
        showInstallButton.value = true
      }
      
      // 桌面浏览器：监听beforeinstallprompt事件
      window.addEventListener('beforeinstallprompt', (e) => {
        e.preventDefault()
        deferredPrompt.value = e
        showInstallButton.value = true
      })
      
      // 监听appinstalled事件（iOS和桌面）
      window.addEventListener('appinstalled', () => {
        showInstallButton.value = false
        deferredPrompt.value = null
      })
      
      // 桌面浏览器：如果3秒后未触发beforeinstallprompt，也显示按钮
      if (!isIOS.value && !isAndroid.value) {
        setTimeout(() => {
          if (!showInstallButton.value && !isPWA()) {
            showInstallButton.value = true
          }
        }, 3000)
      }
      
      // 最后检查token
      checkToken()
    })
    
    // 安装PWA
    const installPWA = async () => {
      // iOS设备：显示安装引导
      if (isIOS.value) {
        alert(`📱 iOS设备安装步骤：\n\n1. 点击底部中间的"分享"按钮 (⬆️)\n2. 向下滚动找到"添加到主屏幕"\n3. 点击"添加"完成安装\n4. 应用名称：${brand.title}`)
        return
      }
      
      // Android设备：直接下载APK
      if (isAndroid.value) {
        // 直接下载APK文件
        const downloadUrl = 'https://download.xbyl28.com/xb.apk'
        const link = document.createElement('a')
        link.href = downloadUrl
        link.download = 'xb.apk'
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        return
      }
      
      // 桌面浏览器：尝试使用标准API
      if (deferredPrompt.value) {
        try {
          deferredPrompt.value.prompt()
          await deferredPrompt.value.userChoice
          deferredPrompt.value = null
        } catch (error) {
          console.error('[桌面] 安装出错:', error)
          alert('无法安装PWA，请确保：\n1. 使用Chrome或Edge浏览器\n2. 使用HTTPS访问\n3. 未安装过此应用')
        }
      } else {
        alert('您的浏览器不支持一键安装。\n\n请使用Chrome或Edge浏览器访问。')
      }
    }
    
    // 使用构建时确定的背景图
    const bgImage = getBgImage()
    const bgStyle = computed(() => ({
      backgroundImage: `url(${bgImage})`,
    }))
    
    return {
      account,
      password,
      gameStore,
      router,
      checkToken,
      showInstallButton,
      installPWA,
      isK8Domain,
      bgStyle,
    }
  },
  methods : {
    gotoPlaza(){
      this.$router.push("/plaza")
    },
    gotoRegister(){
      this.$router.push("/register")
    },
    gotoRoute(){
      this.$router.push("/netway")
    },

    onLogin()
    {
      this.gameStore.login({
        name:this.account,
        pwd:this.password,
      }).then((response)=>{
        console.warn(">>>>onLogin>>ok>>", response)
        // 只有登录成功才跳转
        this.$router.push("/plaza")
      }).catch((error)=>{
        console.warn(">>>>onLogin>>error>>", error)
        // 登录失败不跳转，用户会看到错误提示
      })
    },
  },
}
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped lang="scss">
.install-btn {
  position: fixed;
  top: 15px;
  right: 20px;
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 10px 20px;
  background: linear-gradient(135deg, #facc15 0%, #fbbf24 100%);
  border: none;
  border-radius: 25px;
  color: #000000;
  font-size: 0.9rem;
  font-weight: bold;
  cursor: pointer;
  box-shadow: 0 4px 12px rgba(250, 204, 21, 0.4);
  transition: all 0.3s ease;
  backdrop-filter: blur(10px);

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(250, 204, 21, 0.6);
  }

  &:active {
    transform: scale(0.96);
  }

  span {
    font-weight: 600;
    letter-spacing: 1px;
  }
}

.bg {
  background-image: url("../assets/bg.jpg");
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  background-attachment: fixed;
  position: relative;
  display: flex;
  flex-direction: column;
  height: 100vh;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.1);
    z-index: 1;
  }
}

.main {
  position: relative;
  z-index: 2;
  justify-content: flex-end;
  margin-top: auto;
  margin-bottom: 100px;
}

.input-group {
  display: flex;
  align-items: center;
  width: 85%;
  max-width: 350px;
  height: 50px;
  background: rgba(255, 255, 255, 0.08);
  border: 2px solid rgba(250, 204, 21, 0.4);
  border-radius: 12px;
  padding: 0 15px;
  backdrop-filter: blur(10px);
  transition: all 0.3s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.12);
    border-color: rgba(250, 204, 21, 0.7);
    box-shadow: 0 8px 16px rgba(0, 0, 0, 0.2);
  }

  &:focus-within {
    background: rgba(255, 255, 255, 0.15);
    border-color: #facc15;
    box-shadow: 0 8px 24px rgba(250, 204, 21, 0.25);
  }

  .input-icon {
    font-size: 1.3rem;
    color: #facc15;
    margin-right: 12px;
    flex-shrink: 0;
  }

  .input-field {
    flex: 1;
    background: transparent;
    border: none;
    outline: none;
    color: #ffffff;
    font-size: 0.95rem;
    font-weight: 500;
    letter-spacing: 0.5px;

    &::placeholder {
      color: rgba(255, 255, 255, 0.5);
    }

    &:focus {
      outline: none;
    }
  }
}

.button-group {
  display: flex;
  gap: 15px;
  width: 85%;
  max-width: 350px;
}

.secondary-buttons {
  display: flex;
  gap: 20px;
  justify-content: center;
  width: 85%;
  max-width: 350px;
}

.btn {
  flex: 1;
  height: 48px;
  border: none;
  border-radius: 10px;
  font-size: 1rem;
  font-weight: bold;
  letter-spacing: 2px;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: rgba(255, 255, 255, 0.15);
    transition: left 0.3s ease;
    z-index: 0;
  }

  span {
    position: relative;
    z-index: 1;
  }

  &:hover::before {
    left: 0;
  }

  &:active {
    transform: scale(0.98);
  }
}

.btn-primary {
  background: linear-gradient(135deg, #facc15 0%, #fbbf24 100%);
  color: #000000;
  box-shadow: 0 4px 12px rgba(250, 204, 21, 0.3);

  &:hover {
    box-shadow: 0 6px 20px rgba(250, 204, 21, 0.5);
    transform: translateY(-2px);
  }
}

.btn-large {
  width: 85%;
  max-width: 350px;
  height: 48px;
  font-size: 1rem;
}

.btn-text {
  width: 140px;
  height: 44px;
  padding: 0;
  background: rgba(31, 41, 55, 0.6);
  border: 2px solid rgba(250, 204, 21, 0.4);
  border-radius: 8px;
  font-size: 0.95rem;
  color: rgba(255, 255, 255, 0.9);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
  backdrop-filter: blur(8px);
  transition: all 0.3s ease;

  span {
    font-weight: 600;
    letter-spacing: 1px;
  }

  &:hover {
    color: #facc15;
    background: rgba(31, 41, 55, 0.8);
    border-color: #facc15;
    box-shadow: 0 6px 16px rgba(250, 204, 21, 0.2);
    transform: translateY(-2px);

    span {
      text-shadow: 0 0 12px rgba(250, 204, 21, 0.5);
    }
  }

  &:active {
    transform: scale(0.96);
  }

  &::before {
    display: none;
  }
}

.btn-secondary {
  background: rgba(31, 41, 55, 0.8);
  color: #ffffff;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
  border: 2px solid rgba(250, 204, 21, 0.5);
  backdrop-filter: blur(10px);

  &:hover {
    box-shadow: 0 6px 20px rgba(250, 204, 21, 0.3);
    border-color: #facc15;
    transform: translateY(-2px);
  }
}

.mt-30 {
  margin-top: 30px;
}

.mt-20 {
  margin-top: 20px;
}
</style>
